import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { fmtDateTime } from "@/lib/format";
import { DOC_STATUS_MAP } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, Loader2, Inbox, CheckCircle2 } from "lucide-react";

export default function Documents() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assignDoc, setAssignDoc] = useState(null);

  const { data: cats } = useQuery({ queryKey: ["doc-cats"], queryFn: async () => (await api.get("/documents/categories")).data });
  const { data, isLoading } = useQuery({ queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data });
  const { data: queue } = useQuery({ queryKey: ["doc-queue"], queryFn: async () => (await api.get("/documents/review-queue")).data });
  const { data: custData } = useQuery({ queryKey: ["customers-min-docs"], queryFn: async () => (await api.get("/customers?limit=200")).data });

  const refetch = () => { qc.invalidateQueries({ queryKey: ["documents"] }); qc.invalidateQueries({ queryKey: ["doc-queue"] }); };

  const columns = [
    { key: "filename", header: "Dokument", render: (d) => (
      <div className="flex items-center gap-2.5"><FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate">{d.filename}</span></div>
    ) },
    { key: "category", header: "Kategorie", mobileHidden: true, render: (d) => (cats?.categories || []).find((c) => c.key === d.category)?.label || d.category },
    { key: "customer_name", header: "Kunde", render: (d) => d.customer_name || <span className="text-muted-foreground">nicht zugeordnet</span> },
    { key: "created_at", header: "Hochgeladen", mobileHidden: true, render: (d) => fmtDateTime(d.created_at) },
    { key: "status", header: "Status", render: (d) => <StatusBadge status={d.status} map={DOC_STATUS_MAP} /> },
  ];

  return (
    <div>
      <PageHeader title="Dokumente" description="Mandantenbezogenes Dokumentenarchiv mit Prüfwarteschlange"
        actions={can(user?.role, "documents:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setUploadOpen(true)} data-testid="upload-document-button">
            <Upload className="h-4 w-4" /> Hochladen
          </Button>
        )} />

      <Tabs defaultValue="archive">
        <TabsList>
          <TabsTrigger value="archive" data-testid="doc-tab-archive">Archiv ({data?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="queue" data-testid="doc-tab-queue">Ungeprüfte Dokumente ({queue?.total ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="archive" className="mt-4">
          <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="document-row"
            onRowClick={(d) => window.open(`${api.defaults.baseURL}/documents/${d.id}/download`, "_blank")} />
        </TabsContent>
        <TabsContent value="queue" className="mt-4">
          {(queue?.items || []).length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Keine ungeprüften Dokumente" description="Alle hochgeladenen Dokumente wurden zugeordnet." />
          ) : (
            <div className="space-y-2">
              {queue.items.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0"><Inbox className="h-4 w-4 shrink-0 text-amber-600" />
                    <div className="min-w-0"><div className="truncate text-sm font-medium">{d.filename}</div>
                      <div className="text-xs text-muted-foreground">{fmtDateTime(d.created_at)}</div></div></div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="ghost" size="sm" asChild><a href={`${api.defaults.baseURL}/documents/${d.id}/download`} target="_blank" rel="noreferrer">Ansehen</a></Button>
                    {can(user?.role, "documents:review") && <Button size="sm" variant="outline" onClick={() => setAssignDoc(d)} data-testid={`assign-doc-${d.id}`}>Zuordnen</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} cats={cats?.categories || []} customers={custData?.items || []} onSaved={refetch} fileRef={fileRef} />
      <AssignDialog doc={assignDoc} onOpenChange={() => setAssignDoc(null)} cats={cats?.categories || []} customers={custData?.items || []} onSaved={refetch} />
    </div>
  );
}

function UploadDialog({ open, onOpenChange, cats, customers, onSaved, fileRef }) {
  const [category, setCategory] = useState("sonstiges");
  const [customerId, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Bitte eine Datei wählen");
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category); fd.append("customer_id", customerId);
    setBusy(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(customerId ? "Dokument hochgeladen und zugeordnet" : "Dokument hochgeladen — in Prüfwarteschlange");
      onSaved(); onOpenChange(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Dokument hochladen</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Datei (PDF, JPEG, PNG · max. 10 MB)</Label>
            <Input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" capture="environment" data-testid="document-file-input" /></div>
          <div className="space-y-1.5"><Label>Kategorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Kunde (optional)</Label>
            <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Später zuordnen" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Später zuordnen (Prüfwarteschlange)</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="upload-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Hochladen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ doc, onOpenChange, cats, customers, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!customerId) return toast.error("Bitte einen Kunden wählen");
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/assign`, { customer_id: customerId, category: category || doc.category });
      toast.success("Dokument zugeordnet"); onSaved(); onOpenChange();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Dokument zuordnen</DialogTitle></DialogHeader>
        {doc && <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">{doc.filename}</p>
          <div className="space-y-1.5"><Label>Kunde</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="assign-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Kategorie</Label>
            <Select value={category || doc.category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onOpenChange}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="assign-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zuordnen
            </Button>
          </DialogFooter>
        </form>}
      </DialogContent>
    </Dialog>
  );
}
